//
// # Color Theming
//

// Enumerated Themes
export type Theme = "light" | "dark" | "high-contrast" | "high-contrast-light";

// The four theme values, each typed as the `Theme` union so they are assignable
// wherever a `Theme` is wanted — without a type assertion. Defined as standalone
// consts (not object properties referenced as `Theme.X`) so the `Theme` object
// below carries no self-reference, which would otherwise widen these back to
// `string` during inference.
const Light: Theme = "light";
const Dark: Theme = "dark";
const HighContrast: Theme = "high-contrast";
const HighContrastLight: Theme = "high-contrast-light";

// "The" global module-scope theme
let theTheme: Theme = Dark;

const get = (): Readonly<Theme> => theTheme;

// Pure state setter — no DOM. The browser-side background application that used
// to live here moved out to the callers (see `applyThemeToDom` in EditorCore) so
// this render-core module stays free of DOM globals and type-checks under a
// non-DOM lib (the headless server render path).
const set = (theme: Theme): void => {
  theTheme = theme;
};

// Check if theme is a dark variant
const isDark = (theme: Theme): boolean =>
  theme === Dark || theme === HighContrast;

// Check if theme is a high contrast variant
const isHighContrast = (theme: Theme): boolean =>
  theme === HighContrast || theme === HighContrastLight;

// Toggle between light and dark (used by VSCode extension).
// The IDE uses its own Light/Dark/Auto cycle via UserPreferences.
const toggle = (): void => {
  set(isDark(theTheme) ? Light : Dark);
};

export const Theme = {
  // The four theme values
  Light,
  Dark,
  HighContrast,
  HighContrastLight,
  // Accessor methods
  get,
  set,
  isDark,
  isHighContrast,
  toggle,
};
